import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../../components/Card';
import { Loading } from '../../components/Loading';
import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell 
} from 'recharts';
import { 
  TrendingUp, TrendingDown, Users, Clock, CheckCircle, 
  AlertTriangle, Activity, FileText, BarChart3,
  PieChartIcon, TrendingUpIcon, Calendar, Download
} from 'lucide-react';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';
import { useApiData } from '@/hooks/useDataMode';
import { analyticsService, type ExecutiveDashboardMetrics } from '@/services/analyticsService';
import { buildTrendSeries } from './reportsOverviewTrends';
import { exportService } from '@/services/exportService';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

// Using types from analyticsService
type DashboardMetrics = ExecutiveDashboardMetrics;

interface TrendData {
  date: string;
  tickets: number;
  /** Hours, matching summary.averageResponseTimeHours and the Avg Response Time card. */
  responseTime: number;
  satisfaction: number;
}

interface CategoryData {
  name: string;
  value: number;
  percentage: number;
  color: string;
}

const ReportsOverview: React.FC = () => {
  useDocumentTitle('Reports Overview | SupportDesk');
  const [dateRange, setDateRange] = useState(30);
  const [customRange, setCustomRange] = useState<{ from: Date; to: Date } | null>(null);
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [customFrom, setCustomFrom] = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [customTo, setCustomTo] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  // Convert dateRange to actual dates
  const dateRangeParams = useMemo(() => {
    if (customRange) {
      return customRange;
    }
    return {
      from: startOfDay(subDays(new Date(), dateRange)),
      to: endOfDay(new Date())
    };
  }, [dateRange, customRange]);

  // Generate mock data for demo mode
  const generateMockData = (): DashboardMetrics => {
    // Generate trend data
    const ticketTrends = [];
    for (let i = dateRange - 1; i >= 0; i--) {
      ticketTrends.push({
        date: format(subDays(new Date(), i), 'MMM dd'),
        created: Math.floor(Math.random() * 50) + 30,
        resolved: Math.floor(Math.random() * 45) + 25,
        backlog: Math.floor(Math.random() * 20) + 10,
      });
    }

    return {
      totalTickets: 2456,
      openTickets: 189,
      closedTickets: 2267,
      avgResponseTime: 2.4, // in hours
      avgResolutionTime: 4.2, // in hours
      customerSatisfactionAvg: 4.3,
      slaComplianceRate: 92,
      agentUtilization: 87,
      ticketTrends,
      categoryDistribution: [
        { category: 'Technical Support', count: 892, percentage: 36.3 },
        { category: 'Billing', count: 567, percentage: 23.1 },
        { category: 'Account Management', count: 423, percentage: 17.2 },
        { category: 'Product Inquiries', count: 334, percentage: 13.6 },
        { category: 'Other', count: 240, percentage: 9.8 }
      ],
      priorityDistribution: [
        { priority: 'High', count: 234, percentage: 9.5 },
        { priority: 'Medium', count: 1456, percentage: 59.3 },
        { priority: 'Low', count: 766, percentage: 31.2 }
      ]
    };
  };

  // Fetch dashboard data with hybrid approach
  const {
    data: rawMetrics,
    loading,
    error,
    refetch,
    isUsingMockData
  } = useApiData(
    () => analyticsService.getExecutiveDashboard(dateRangeParams),
    generateMockData(),
    // #142: on error this page must show its error state, not synthetic analytics. The
    // banner is gated on `error && !isUsingMockData`, so falling back hid the failure.
    { fallbackToMockOnError: false }
  );

  // Ensure metrics has safe defaults to prevent undefined errors
  const metrics = useMemo(() => {
    if (!rawMetrics) {
      return {
        totalTickets: null,
        openTickets: null,
        closedTickets: null,
        avgResponseTime: null,
        avgResolutionTime: null,
        customerSatisfactionAvg: null,
        slaComplianceRate: null,
        agentUtilization: null,
        activeAgentsCount: null,
        ticketTrends: [],
        responseTimeTrend: [],
        satisfactionTrend: [],
        categoryDistribution: [],
        priorityDistribution: []
      };
    }

    const anyRaw = rawMetrics as any;
    const summary = anyRaw?.summary || anyRaw?.data?.summary || {};
    return {
      totalTickets: anyRaw?.totalTickets ?? summary?.totalTicketsThisMonth ?? summary?.totalTicketsToday ?? null,
      openTickets: anyRaw?.openTickets ?? summary?.openTickets ?? null,
      closedTickets: anyRaw?.closedTickets ?? summary?.closedTickets ?? null,
      avgResponseTime: anyRaw?.avgResponseTime ?? summary?.averageResponseTimeHours ?? null,
      avgResolutionTime: anyRaw?.avgResolutionTime ?? summary?.averageResolutionTimeHours ?? null,
      customerSatisfactionAvg: anyRaw?.customerSatisfactionAvg ?? summary?.customerSatisfactionScore ?? null,
      slaComplianceRate: anyRaw?.slaComplianceRate ?? summary?.slaCompliancePercentage ?? null,
      agentUtilization: anyRaw?.agentUtilization ?? summary?.teamUtilizationPercentage ?? null,
      activeAgentsCount: anyRaw?.activeAgentsCount ?? summary?.activeAgentsCount ?? null,
      ticketTrends: anyRaw?.ticketTrends ?? anyRaw?.trends?.ticketVolumeTrend ?? [],
      // Real per-day series the transform below reads instead of inventing values.
      responseTimeTrend: anyRaw?.trends?.responseTimeTrend ?? [],
      satisfactionTrend: anyRaw?.trends?.satisfactionTrend ?? [],
      categoryDistribution: anyRaw?.categoryDistribution ?? [],
      priorityDistribution: anyRaw?.priorityDistribution ?? []
    };
  }, [rawMetrics]);

  // Series construction lives in reportsOverviewTrends so it can be tested without
  // rendering this page. See BugHerd #142.
  const trends: TrendData[] = useMemo(
    () => buildTrendSeries(metrics, date => format(new Date(date), 'MMM dd')),
    [metrics?.ticketTrends, metrics?.responseTimeTrend, metrics?.satisfactionTrend]
  );

  const categories: CategoryData[] = useMemo(() => {
    if (!metrics?.categoryDistribution || !Array.isArray(metrics.categoryDistribution)) return [];
    const colors = ['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#6B7280'];
    return metrics.categoryDistribution.map((cat, index) => ({
      name: cat?.category || 'Unknown',
      value: cat?.count || 0,
      percentage: cat?.percentage || 0,
      color: colors[index % colors.length]
    }));
  }, [metrics?.categoryDistribution]);

  const getChangeIcon = (value: number) => {
    if (value > 0) return <TrendingUp className="w-4 h-4 text-green-500" />;
    if (value < 0) return <TrendingDown className="w-4 h-4 text-red-500" />;
    return null;
  };

  // Card subtitles were unconditional constants ("2,456 tickets", "24 active agents",
  // "4.3/5.0 rating", "92% compliance") shown regardless of the response. They now read
  // from the live summary, and say so plainly when a figure is not available.
  const stat = (value: number | null | undefined, format: (n: number) => string): string =>
    typeof value === 'number' && Number.isFinite(value) ? format(value) : 'Not available';

  const reportCards = [
    {
      title: 'Ticket Analytics',
      description: 'Detailed analysis of ticket volume, types, and resolution metrics',
      icon: <FileText className="w-6 h-6" />,
      link: '/analytics/operational',
      color: 'bg-blue-500',
      stats: stat(metrics?.totalTickets, n => `${n.toLocaleString()} tickets`)
    },
    {
      title: 'Agent Performance',
      description: 'Individual and team performance metrics and productivity analysis',
      icon: <Users className="w-6 h-6" />,
      link: '/reports/agents',
      color: 'bg-green-500',
      stats: stat(metrics?.activeAgentsCount, n => `${n.toLocaleString()} active agents`)
    },
    {
      title: 'SLA Compliance',
      description: 'Service level agreement tracking and compliance reports',
      icon: <Clock className="w-6 h-6" />,
      link: '/sla/dashboard',
      color: 'bg-purple-500',
      stats: stat(metrics?.slaComplianceRate, n => `${n.toFixed(0)}% compliance`)
    }
  ];

  // Handle refresh
  const handleRefresh = () => {
    refetch();
  };

  // Handle CSV exports
  const handleExportAll = async () => {
    setExportError(null);
    setIsExporting(true);
    try {
      await exportService.exportAllReports({
        fromDate: dateRangeParams.from,
        toDate: dateRangeParams.to,
      });
    } catch (error) {
      console.error('Export failed:', error);
      setExportError('Unable to export reports. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportAgents = async () => {
    setExportError(null);
    setIsExporting(true);
    try {
      await exportService.exportAgentPerformance({
        fromDate: dateRangeParams.from,
        toDate: dateRangeParams.to,
      });
    } catch (error) {
      console.error('Agent performance export failed:', error);
      setExportError('Unable to export agent performance. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportTickets = async () => {
    setExportError(null);
    setIsExporting(true);
    try {
      await exportService.exportTicketAnalytics({
        fromDate: dateRangeParams.from,
        toDate: dateRangeParams.to,
      });
    } catch (error) {
      console.error('Ticket analytics export failed:', error);
      setExportError('Unable to export ticket analytics. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loading size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {exportError && <div role="alert" className="rounded-md bg-red-50 p-4 text-red-700">{exportError}</div>}
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reports Overview</h1>
          <p className="text-gray-600 mt-1">
            Comprehensive analytics and insights for your support operations
            {isUsingMockData && (
              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                Demo Data
              </span>
            )}
          </p>
        </div>
        {/* flex-wrap keeps the range selector and Export All inside a 320px viewport
            instead of forcing a page-level horizontal scrollbar (BugHerd #147). */}
        <div className="flex flex-wrap gap-2">
          <select
            className="px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            value={customRange ? 'custom' : dateRange}
            onChange={(e) => {
              if (e.target.value === 'custom') {
                setShowCustomModal(true);
              } else {
                setCustomRange(null);
                setDateRange(Number(e.target.value));
              }
            }}
          >
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            {customRange && <option value="custom">Custom Range</option>}
          </select>
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleRefresh}
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Refresh'}
          </Button>
          <Button 
            variant={customRange ? "primary" : "outline"} 
            size="sm"
            onClick={() => setShowCustomModal(true)}
          >
            <Calendar className="w-4 h-4 mr-2" />
            {customRange ? `${format(customRange.from, 'MMM dd')} - ${format(customRange.to, 'MMM dd')}` : 'Custom Range'}
          </Button>
          <Button 
            variant="primary" 
            size="sm"
            onClick={handleExportAll}
            disabled={isExporting || loading}
          >
            <Download className="w-4 h-4 mr-2" />
            {isExporting ? 'Exporting...' : 'Export All'}
          </Button>
        </div>
      </div>

      {/* Custom Date Range Modal */}
      {showCustomModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6 shadow-xl space-y-4">
            <h3 className="text-lg font-bold text-gray-900">Select Custom Date Range</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
                <input
                  type="date"
                  className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-primary-500 focus:border-primary-500"
                  value={customFrom}
                  onChange={(e) => setCustomFrom(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
                <input
                  type="date"
                  className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-primary-500 focus:border-primary-500"
                  value={customTo}
                  onChange={(e) => setCustomTo(e.target.value)}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowCustomModal(false)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => {
                  if (customFrom && customTo) {
                    setCustomRange({
                      from: startOfDay(new Date(customFrom)),
                      to: endOfDay(new Date(customTo))
                    });
                    setShowCustomModal(false);
                  }
                }}
              >
                Apply Range
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Error state */}
      {error && !isUsingMockData && (
        <Card>
          <div className="p-6">
            <div className="rounded-md bg-red-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <AlertTriangle className="h-5 w-5 text-red-400" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">Error loading reports data</h3>
                  <p className="mt-1 text-sm text-red-700">{error.message}</p>
                  <div className="mt-4">
                    <Button 
                      onClick={handleRefresh} 
                      variant="outline"
                      size="sm"
                    >
                      Try Again
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Tickets</p>
                <p className="text-2xl font-bold mt-1">{stat(metrics?.totalTickets, n => n.toLocaleString())}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>
        </Card>

        <Card>
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Avg Response Time</p>
                <p className="text-2xl font-bold mt-1">
                  {stat(metrics?.avgResponseTime, n => n < 1 ? `${(n * 60).toFixed(0)} min` : `${n.toFixed(1)} hrs`)}
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <Clock className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>
        </Card>



        <Card>
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">SLA Compliance</p>
                <p className="text-2xl font-bold mt-1">{stat(metrics?.slaComplianceRate, n => `${n.toFixed(0)}%`)}</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Ticket Volume Trend */}
        <Card>
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Ticket Volume Trend</h3>
              <Badge variant="info">Live</Badge>
            </div>
            {trends && trends.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={trends}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Area type="monotone" dataKey="tickets" stroke="#3B82F6" fill="#93BBFC" name="Created" />
                </AreaChart>
              </ResponsiveContainer>
            ) : null}
            {trends && trends.length > 0 ? (
              <p className="mt-2 text-xs text-gray-500" data-testid="resolved-trend-unavailable">
                Resolved volume per day is not available from the analytics API, so only
                created volume is shown.
              </p>
            ) : (
              <div className="flex items-center justify-center h-[250px] text-gray-500">
                No trend data available
              </div>
            )}
          </div>
        </Card>

        {/* Category Distribution */}
        <Card>
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Ticket Categories</h3>
            </div>
            {categories && categories.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={categories}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(entry) => `${entry?.name || 'Unknown'}: ${(entry?.percentage || 0).toFixed(1)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {categories.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry?.color || '#6B7280'} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[250px] text-gray-500">
                No category data available
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Performance Metrics */}
      <Card>
        <div className="p-6">
          <h3 className="text-lg font-semibold mb-4">Performance Metrics</h3>
          {metrics.responseTimeTrend.some((point: { value?: number }) => Number(point.value) > 0) ? (
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={trends}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis yAxisId="left" />

                <Tooltip />
                <Line yAxisId="left" type="monotone" dataKey="responseTime" stroke="#F59E0B" name="Response Time (hrs)" />

              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[250px] text-gray-500">
              No performance data available
            </div>
          )}
        </div>
      </Card>

      {/* Report Cards */}
      <div>
        <h2 className="text-xl font-semibold mb-4">Detailed Reports</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {reportCards && reportCards.length > 0 ? reportCards.map((report) => (
            <Card key={report?.title || 'report'} className="hover:shadow-lg transition-shadow h-full">
              <div className="p-6">
                <div className={`w-12 h-12 ${report?.color || 'bg-gray-500'} rounded-lg flex items-center justify-center text-white mb-4`}>
                  {report?.icon || <FileText className="w-6 h-6" />}
                </div>
                <h3 className="font-semibold text-lg mb-2">{report?.title || 'Unknown Report'}</h3>
                <p className="text-sm text-gray-600 mb-3">{report?.description || 'No description available'}</p>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-primary-600">{report?.stats || 'N/A'}</span>
                  <Link to={report?.link || '#'} className="text-primary-600 hover:text-primary-800">
                    →
                  </Link>
                </div>
                <div className="flex gap-2">
                  <Link to={report?.link || '#'} className="flex-1">
                    <Button variant="outline" size="sm" className="w-full">
                      View Details
                    </Button>
                  </Link>
                  {report.title !== 'SLA Compliance' && <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      if (report?.title === 'Agent Performance') {
                        handleExportAgents();
                      } else if (report?.title === 'Ticket Analytics') {
                        handleExportTickets();
                      } else {
                        handleExportTickets();
                      }
                    }}
                    disabled={isExporting}
                    title={`Export ${report?.title || 'report'} to CSV`}
                    aria-label={`Export ${report?.title || 'report'} to CSV`}
                  >
                    <Download className="w-4 h-4" />
                  </Button>}
                </div>
              </div>
            </Card>
          )) : (
            <div className="col-span-full flex items-center justify-center py-8 text-gray-500">
              No reports available
            </div>
          )}
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <div className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Open Tickets</p>
                <p className="text-xl font-bold">{stat(metrics?.openTickets, n => n.toLocaleString())}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-orange-500" />
            </div>
          </div>
        </Card>

        <Card>
          <div className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Resolved in selected period</p>
                <p className="text-xl font-bold">{stat(metrics?.closedTickets, n => n.toLocaleString())}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </div>
        </Card>

        <Card>
          <div className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Agent Utilization</p>
                <p className="text-xl font-bold">{stat(metrics?.agentUtilization, n => `${Math.round(n)}%`)}</p>
              </div>
              <Users className="w-8 h-8 text-blue-500" />
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ReportsOverview;
